/**
 * Canvas 2D renderer. Runs on requestAnimationFrame, decoupled from the 8Hz
 * sim tick — it just draws whatever the latest applied GameState is.
 */

import { BUILDINGS, TICKS_PER_SECOND, UNITS, canPlaceBuilding, entityCenter, getEntity, isBlocked } from '../sim';
import type { Building, DataNode, Entity, EntityId, GameState, Market, Unit } from '../sim';
import { clampCamera, effectiveTileSize, worldToScreen } from './camera';
import { EffectsSystem } from './effects';
import type { GameSession } from './session';
import {
  BUILDING_FRAME_CADENCE,
  DATA_NODE_CADENCE,
  MARKET_CADENCE,
  PLAYER_COLORS,
  SPRITES,
  UNIT_FRAME_CADENCE,
  frameIndexFor,
} from './sprites';

const GROUND_A = '#1c2430';
const GROUND_B = '#202a37';
const ROCK_COLOR = '#11151c';

/** Per-unit facing/motion memory, used for horizontal-flip and walk bounce. */
interface UnitMotion {
  lastX: number;
  lastY: number;
  facing: 1 | -1;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private minimapCtx: CanvasRenderingContext2D;
  private effects = new EffectsSystem();
  private unitMotion = new Map<EntityId, UnitMotion>();
  private lastMotionCleanupTick = -1;
  private lastSelectionKey = '';
  private selectionNudgePending = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private minimapCanvas: HTMLCanvasElement,
    private session: GameSession,
    /** Rect of whatever HUD panel covers the bottom of the viewport (the
     * selection panel), or null. Drives camera overscroll + selection nudge. */
    private getBottomPanelRect: () => DOMRect | null = () => null,
  ) {
    this.ctx = canvas.getContext('2d')!;
    this.minimapCtx = minimapCanvas.getContext('2d')!;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  get cssWidth(): number {
    return this.canvas.clientWidth;
  }

  get cssHeight(): number {
    return this.canvas.clientHeight;
  }

  /** On-screen pixel size of one world tile at the current zoom. */
  private tileSize(): number {
    return effectiveTileSize(this.session.camera);
  }

  /** Units are drawn a couple px smaller than a full tile so sprites don't collide edge-to-edge. */
  private unitDrawSize(): number {
    return this.tileSize() - 2;
  }

  /** When a fresh selection sits under the bottom selection panel, pan the
   * camera down so the selected units clear it (the camera's bottomInsetPx
   * makes room even at the map's bottom edge). Deferred one frame after the
   * selection changes so the HUD has rendered the panel at its final size
   * before we measure the overlap — hud.update() runs after render() in the
   * frame loop. */
  private maybeNudgeSelectionIntoView(panelRect: DOMRect | null, s: GameState, w: number, h: number): void {
    const key = this.session.selection.join(',');
    if (key !== this.lastSelectionKey) {
      this.lastSelectionKey = key;
      this.selectionNudgePending = key.length > 0;
      return;
    }
    if (!this.selectionNudgePending) return;
    this.selectionNudgePending = false;
    if (!panelRect) return;

    const ts = this.tileSize();
    const half = ts / 2;
    let overlap = 0;
    for (const id of this.session.selection) {
      const e = getEntity(s, id);
      if (!e) continue;
      const c = entityCenter(e);
      const scr = worldToScreen(this.session.camera, c.x, c.y);
      const underPanel = scr.x + half > panelRect.left && scr.x - half < panelRect.right;
      if (!underPanel) continue;
      overlap = Math.max(overlap, scr.y + half - panelRect.top);
    }
    if (overlap <= 0) return;
    this.session.camera.y += (overlap + 8) / ts;
    clampCamera(this.session.camera, s.map.width, s.map.height, w, h);
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = false;
  }

  render(): void {
    const ctx = this.ctx;
    const w = this.cssWidth;
    const h = this.cssHeight;
    ctx.clearRect(0, 0, w, h);
    const s = this.session.state;
    if (!s) return;

    const now = performance.now();
    // Give the camera overscroll room past the map's bottom edge whenever a
    // HUD panel covers the bottom of the viewport, so bottom-of-map content
    // can be panned up above it. Stored on the camera so input.ts's own
    // clampCamera calls (pan/zoom) respect the same bound.
    const panelRect = this.getBottomPanelRect();
    this.session.camera.bottomInsetPx = panelRect ? Math.max(0, h - panelRect.top) : 0;
    clampCamera(this.session.camera, s.map.width, s.map.height, w, h);
    this.maybeNudgeSelectionIntoView(panelRect, s, w, h);

    this.drawTerrain(ctx, s, w, h);
    this.effects.update(s, now, (x, y, kind) => {
      const fog = this.session.fogAt(Math.floor(x), Math.floor(y));
      return kind === 'unit' ? fog === 2 : fog >= 1;
    });
    this.drawEntities(ctx, s, now);
    this.effects.draw(ctx, this.session.camera, now);
    this.drawPlacementGhost(ctx, s);
    this.drawDragBox(ctx);
    this.renderMinimap(s);

    // Prune per-unit motion memory for dead units once per sim tick (cheap,
    // and keeps this Map from growing unbounded over a long match).
    if (s.tick !== this.lastMotionCleanupTick) {
      this.lastMotionCleanupTick = s.tick;
      if (this.unitMotion.size > 0) {
        const live = new Set<EntityId>();
        for (const e of s.entities) if (e.kind === 'unit') live.add(e.id);
        for (const id of this.unitMotion.keys()) if (!live.has(id)) this.unitMotion.delete(id);
      }
    }
  }

  private drawTerrain(ctx: CanvasRenderingContext2D, s: GameState, w: number, h: number): void {
    const cam = this.session.camera;
    const map = s.map;
    const ts = this.tileSize();
    const x0 = Math.max(0, Math.floor(cam.x));
    const y0 = Math.max(0, Math.floor(cam.y));
    const x1 = Math.min(map.width - 1, Math.ceil(cam.x + w / ts));
    const y1 = Math.min(map.height - 1, Math.ceil(cam.y + h / ts));
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const fog = this.session.fogAt(tx, ty);
        const { x: sx, y: sy } = worldToScreen(cam, tx, ty);
        const terrain = map.terrain[ty * map.width + tx];
        ctx.fillStyle = terrain === 1 ? ROCK_COLOR : (tx + ty) % 2 === 0 ? GROUND_A : GROUND_B;
        ctx.fillRect(sx, sy, ts + 1, ts + 1);
        if (fog !== 2) {
          ctx.fillStyle = fog === 0 ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)';
          ctx.fillRect(sx, sy, ts + 1, ts + 1);
        }
      }
    }
  }

  private drawEntities(ctx: CanvasRenderingContext2D, s: GameState, now: number): void {
    const me = this.session.myPlayer;
    const list = s.entities.filter((e) => {
      if (e.owner === me) return true;
      const c = entityCenter(e);
      const fog = this.session.fogAt(Math.floor(c.x), Math.floor(c.y));
      if (e.kind === 'unit') return fog === 2;
      return fog >= 1;
    });
    const order: Record<Entity['kind'], number> = { dataNode: 0, market: 0, building: 1, unit: 2 };
    list.sort((a, b) => order[a.kind] - order[b.kind]);
    for (const e of list) {
      if (e.kind === 'dataNode') this.drawDataNode(ctx, e, now);
      else if (e.kind === 'market') this.drawMarket(ctx, e, now);
      else if (e.kind === 'building') this.drawBuilding(ctx, e, now);
      else if (e.kind === 'unit') this.drawUnit(ctx, e, now);
    }
  }

  private drawHpBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, frac: number): void {
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(x, y, w, 4);
    ctx.fillStyle = frac > 0.5 ? '#46a758' : frac > 0.25 ? '#e6b34f' : '#e5484d';
    ctx.fillRect(x, y, w * Math.max(0, frac), 4);
  }

  private drawBuilding(ctx: CanvasRenderingContext2D, b: Building, now: number): void {
    const cam = this.session.camera;
    const bs = BUILDINGS[b.type];
    const { x: sx, y: sy } = worldToScreen(cam, b.pos.x, b.pos.y);
    const size = bs.size * this.tileSize();
    const c = entityCenter(b);
    const fog = this.session.fogAt(Math.floor(c.x), Math.floor(c.y));
    const ghost = b.owner !== this.session.myPlayer && fog !== 2;
    const cadence = BUILDING_FRAME_CADENCE[b.type];
    const frame = cadence ? frameIndexFor(cadence, b.id, now) : 0;
    const sprite = SPRITES.getBuilding(b.type, b.owner, frame);
    const baseAlpha = ghost ? 0.4 : 1;

    if (b.buildTicks > 0) {
      const total = Math.max(1, Math.round(bs.buildTimeSec * TICKS_PER_SECOND));
      const frac = b.buildTicks / total;
      ctx.globalAlpha = baseAlpha * 0.55;
      ctx.drawImage(sprite, sx, sy, size, size);
      ctx.globalAlpha = baseAlpha;
      // scaffold cross-braces over the construction site
      ctx.strokeStyle = 'rgba(234,179,8,0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx, sy + size);
      ctx.lineTo(sx + size, sy);
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx + size, sy + size);
      ctx.stroke();
      // remaining (unbuilt) portion masked out from the top
      ctx.fillStyle = 'rgba(6,8,12,0.6)';
      ctx.fillRect(sx, sy, size, size * frac);
    } else {
      ctx.globalAlpha = baseAlpha;
      ctx.drawImage(sprite, sx, sy, size, size);
    }

    // DDoS: red glitch overlay while the building is knocked offline
    if (b.buildTicks === 0 && (b.disabledTicks ?? 0) > 0 && !ghost) {
      ctx.globalAlpha = 1;
      const pulse = 0.3 + 0.22 * Math.sin(now / 110);
      ctx.fillStyle = `rgba(255,60,60,${pulse})`;
      ctx.fillRect(sx, sy, size, size);
      ctx.strokeStyle = 'rgba(255,92,92,0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(sx + 1, sy + 1, size - 2, size - 2);
      ctx.setLineDash([]);
    }

    // Cyber targeting: highlight visible enemy buildings while an attack is armed
    if (
      this.session.cyberTarget &&
      b.buildTicks === 0 &&
      b.owner !== this.session.myPlayer &&
      b.owner >= 0 &&
      fog === 2
    ) {
      ctx.globalAlpha = 1;
      const pulse = 0.5 + 0.4 * Math.sin(now / 150);
      ctx.strokeStyle = `rgba(255,80,80,${pulse})`;
      ctx.lineWidth = 2;
      ctx.strokeRect(sx - 2, sy - 2, size + 4, size + 4);
    }

    if (this.session.selection.includes(b.id)) {
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = 2;
      ctx.strokeRect(sx - 1, sy - 1, size + 2, size + 2);
    }

    if (b.hp < b.maxHp && b.buildTicks === 0) {
      this.drawHpBar(ctx, sx, sy - 6, size, b.hp / b.maxHp);
    }
    ctx.globalAlpha = 1;
  }

  private drawUnit(ctx: CanvasRenderingContext2D, u: Unit, now: number): void {
    const cam = this.session.camera;
    const { x: sx, y: sy } = worldToScreen(cam, u.pos.x, u.pos.y);
    const size = this.unitDrawSize();
    const half = size / 2;

    // Facing (horizontal flip) + movement detection, from frame-to-frame position deltas.
    let motion = this.unitMotion.get(u.id);
    if (!motion) {
      motion = { lastX: u.pos.x, lastY: u.pos.y, facing: 1 };
      this.unitMotion.set(u.id, motion);
    }
    const dx = u.pos.x - motion.lastX;
    const dy = u.pos.y - motion.lastY;
    const moving = Math.abs(dx) > 0.004 || Math.abs(dy) > 0.004;
    if (Math.abs(dx) > 0.004) motion.facing = dx < 0 ? -1 : 1;
    motion.lastX = u.pos.x;
    motion.lastY = u.pos.y;

    if (u.anomalyTicks > 0) {
      const pulse = 0.5 + 0.5 * Math.sin(now / 120);
      ctx.strokeStyle = `rgba(255,${Math.round(50 * pulse)},${Math.round(50 * pulse)},1)`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(sx, sy, half + 5, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Frame selection: engineer/scoutCrawler animate their walk cycle only
    // while actually moving; everything else with a cadence animates continuously.
    const cadence = UNIT_FRAME_CADENCE[u.type];
    let frame = 0;
    if (cadence) {
      const gateToMovement = u.type === 'engineer' || u.type === 'scoutCrawler';
      if (!gateToMovement || moving) frame = frameIndexFor(cadence, u.id, now);
    }

    // Cosmetic vertical offset: continuous hover bob for the floating inference
    // agent, a subtle walk bounce for grounded units while moving.
    let yOffset = 0;
    if (u.type === 'inferenceAgent') {
      yOffset = Math.sin(now / 300 + u.id) * 1.2;
    } else if (moving) {
      yOffset = Math.sin(now / 130 + u.id * 2) * 0.8;
    }

    const sprite = SPRITES.getUnit(u.type, u.owner, frame, motion.facing === -1);
    ctx.drawImage(sprite, sx - half, sy - half + yOffset, size, size);

    if (u.type === 'engineer' && u.carryingData > 0) {
      const scale = size / 16;
      const bagGridX = motion.facing === -1 ? 4 : 10;
      ctx.fillStyle = '#2dd4bf';
      ctx.fillRect(sx - half + bagGridX * scale, sy - half + 9 * scale + yOffset, 2 * scale, 2 * scale);
    }

    if (this.session.selection.includes(u.id)) {
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sx, sy, half + 5, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (u.hp < u.maxHp) {
      this.drawHpBar(ctx, sx - half, sy - half - 7, size, u.hp / u.maxHp);
    }
  }

  private drawDataNode(ctx: CanvasRenderingContext2D, n: DataNode, now: number): void {
    const cam = this.session.camera;
    const { x: sx, y: sy } = worldToScreen(cam, n.pos.x, n.pos.y);
    const size = this.unitDrawSize();
    const half = size / 2;
    const frame = frameIndexFor(DATA_NODE_CADENCE, n.id, now);
    ctx.drawImage(SPRITES.getDataNode(frame), sx - half, sy - half, size, size);
    ctx.fillStyle = '#eafffb';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(String(Math.round(n.remaining)), sx, sy + half + 11);
  }

  private drawMarket(ctx: CanvasRenderingContext2D, m: Market, now: number): void {
    const cam = this.session.camera;
    const { x: sx, y: sy } = worldToScreen(cam, m.pos.x, m.pos.y);
    const size = this.unitDrawSize() + 4;
    const half = size / 2;
    const frame = frameIndexFor(MARKET_CADENCE, m.id, now);
    ctx.drawImage(SPRITES.getMarket(frame), sx - half, sy - half, size, size);
    // premium segments (frontier-race prize) get a gold ring + star
    if (m.tier === 'premium') {
      ctx.strokeStyle = '#ffd75e';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sx, sy, half + 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#ffd75e';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('★', sx, sy - half - 5);
    }
    ctx.fillStyle = '#241a00';
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(m.demand), sx, sy);
  }

  private footprintBlocked(s: GameState, x: number, y: number, size: number): boolean {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        if (isBlocked(s, x + dx, y + dy)) return true;
      }
    }
    return false;
  }

  private drawPlacementGhost(ctx: CanvasRenderingContext2D, s: GameState): void {
    const placement = this.session.placement;
    if (!placement) return;
    const cam = this.session.camera;
    const bs = BUILDINGS[placement.building];
    const tx = Math.floor(placement.cursor.x);
    const ty = Math.floor(placement.cursor.y);
    const { x: sx, y: sy } = worldToScreen(cam, tx, ty);
    const size = bs.size * this.tileSize();
    const p = s.players[this.session.myPlayer];
    // mirror the sim's exact placement rule (bounds margin, terrain,
    // entities) so the ghost never lies about a spot the command would reject
    const clear = canPlaceBuilding(s, placement.building, tx, ty);
    const ok = clear && p.budget >= bs.costBudget && p.data >= bs.costData && p.phase >= bs.phase;
    ctx.globalAlpha = 0.6;
    ctx.drawImage(SPRITES.getGhostBuilding(placement.building, ok), sx, sy, size, size);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = ok ? '#46e678' : '#e64646';
    ctx.lineWidth = 2;
    ctx.strokeRect(sx, sy, size, size);
  }

  private drawDragBox(ctx: CanvasRenderingContext2D): void {
    const box = this.session.dragBox;
    if (!box) return;
    const rx = Math.min(box.x0, box.x1);
    const ry = Math.min(box.y0, box.y1);
    const rw = Math.abs(box.x1 - box.x0);
    const rh = Math.abs(box.y1 - box.y0);
    ctx.fillStyle = 'rgba(79,142,247,0.15)';
    ctx.fillRect(rx, ry, rw, rh);
    ctx.strokeStyle = '#4f8ef7';
    ctx.lineWidth = 1;
    ctx.strokeRect(rx, ry, rw, rh);
  }

  private renderMinimap(s: GameState): void {
    const ctx = this.minimapCtx;
    const size = this.minimapCanvas.width;
    const map = s.map;
    ctx.fillStyle = '#0a0d12';
    ctx.fillRect(0, 0, size, size);
    const sx = size / map.width;
    const sy = size / map.height;
    for (let ty = 0; ty < map.height; ty++) {
      for (let tx = 0; tx < map.width; tx++) {
        const fog = this.session.fogAt(tx, ty);
        if (fog === 0) continue;
        const terrain = map.terrain[ty * map.width + tx];
        ctx.fillStyle = terrain === 1 ? '#2a2a2e' : fog === 2 ? '#33414f' : '#232b33';
        ctx.fillRect(tx * sx, ty * sy, Math.ceil(sx), Math.ceil(sy));
      }
    }
    const me = this.session.myPlayer;
    for (const e of s.entities) {
      if (e.owner !== me) {
        const c = entityCenter(e);
        const fog = this.session.fogAt(Math.floor(c.x), Math.floor(c.y));
        if (e.kind === 'unit' && fog !== 2) continue;
        if (e.kind !== 'unit' && fog < 1) continue;
      }
      const c = entityCenter(e);
      ctx.fillStyle = e.owner === -1 ? '#94a3b8' : PLAYER_COLORS[e.owner % PLAYER_COLORS.length];
      ctx.fillRect(c.x * sx - 1, c.y * sy - 1, 3, 3);
    }
    const cam = this.session.camera;
    const vw = this.cssWidth / this.tileSize();
    const vh = this.cssHeight / this.tileSize();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.strokeRect(cam.x * sx, cam.y * sy, vw * sx, vh * sy);
  }
}
